/**
 * Petstyle24 – Bürstenberatung: Fragenfluss und Ergebnis (sections/brush-quiz.liquid; Gesamtplan Et. 11;
 * Vertrag: docs/quiz/UI_CONTRACT.md; Gestaltung: docs/DESIGN_ABGELEITET.md §9; Regeln: docs/quiz/QUIZ_TABELLE.md).
 *
 * <ps-brush-quiz> steuert nur die Bedienung des servergerenderten Markups:
 *   - Einzelwahl (aria-pressed) führt zur nächsten Frage; Mehrfachwahl (role="checkbox", aria-checked)
 *     bis data-ps-quiz-multi Antworten, danach „Weiter“; „Zurück“ behält die Antworten; Neustart.
 *   - Fortschritt „Frage n von N“ (Vorlage aus data-progress-template, nur textContent) und Balken.
 *   - Fokus auf die Frage bzw. das Ergebnis; Scrollen nur, wenn der Anfang des Blocks unter dem Header
 *     liegt oder unterhalb der halben Viewport-Höhe beginnt (05c §4); bei reduced motion sofort.
 *
 * Ergebnis (UI-Vertrag §2, §3):
 *   - Datensatz nur über Petstyle24.productData.read(this) (Kollektion „Alle Bürsten“, E18); Empfehlung nur über
 *     Petstyle24.quizEngine.evaluate({ schema: 1, answers, products }) mit Zeitlimit 4 s.
 *   - Das Ergebnis wird streng geprüft: bekannter Zustand mit Panel, jede Produkt-ID im Datensatz und mit
 *     Kartenvorlage, Direktkauf nur mit verfügbarer Variante desselben Produkts. Sonst – wie bei fehlender
 *     Engine, Fehler, Zeitlimit oder fehlenden Daten – erscheint not_configured, nie eine erfundene Empfehlung.
 *   - Darstellung: die Produktkarte (context 'quiz') aus <template data-ps-quiz-cards> per importNode; Texte aus
 *     <script data-ps-quiz-text> (Locale) nur per textContent, Links und Sichtbarkeit nur über Attribute.
 *   - „In den Warenkorb“ (FR-9): G1–G3 direkt mit der Variant ID der Engine über Petstyle24.cart.add
 *     („Wird hinzugefügt …“ → „Hinzugefügt“, Warenkorb öffnet); G4 bzw. mehrere Varianten gleicher Breite über den
 *     Kauf-Link der Karte (Größen-Fenster, assets/ps-size-sheet.js). Kein Zugriff auf das Warenkorb-DOM.
 * Kein Netzwerk, kein Speicher, kein Einfügen von HTML.
 */
(() => {
  'use strict';

  if (customElements.get('ps-brush-quiz')) return;

  const SCHEMA = 1;
  const ENGINE_TIMEOUT = 4000;
  // Zustände mit servergerenderter Darstellung (UI-Vertrag §3)
  const STATES = ['clear', 'alt', 'unclear', 'not_available', 'not_configured'];
  const WITH_ITEM = ['clear', 'alt', 'unclear'];
  const SIZE_KEYS = ['G1', 'G2', 'G3', 'G4'];
  const BUY = ['add', 'choose', 'none'];
  const ADDED_MS = 2000;

  const reducedMotion = () => {
    const PS = window.Petstyle24;
    if (PS && typeof PS.prefersReducedMotion === 'function') return PS.prefersReducedMotion();
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const withTimeout = (promise, ms) =>
    new Promise((resolve) => {
      const timer = window.setTimeout(() => resolve(null), ms);
      promise.then(
        (value) => {
          window.clearTimeout(timer);
          resolve(value);
        },
        () => {
          window.clearTimeout(timer);
          resolve(null);
        }
      );
    });

  const isId = (value) => Number.isSafeInteger(value) && value > 0;
  const fillIn = (template, value) => String(template || '').replace('{value}', () => value);
  const isObject = (value) => value !== null && typeof value === 'object';

  const withVariant = (href, id) => {
    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('variant', String(id));
      return url.pathname + url.search;
    } catch (error) {
      return href;
    }
  };

  const setLabel = (button, value) => {
    const label = button.querySelector('.button__label');
    if (label && value) label.textContent = value;
  };

  const setIcon = (button, visible) => {
    const icon = button.querySelector('.button__icon');
    if (icon) icon.hidden = !visible;
  };

  class PsBrushQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.steps = Array.from(this.querySelectorAll('[data-ps-quiz-step]'));
      this.card = this.querySelector('[data-ps-quiz-card]');
      if (!this.steps.length || !this.card) return;
      this.initialized = true;
      this.count = this.querySelector('[data-ps-quiz-count]');
      this.progress = this.querySelector('[data-ps-quiz-progress]');
      this.fill = this.progress ? this.progress.querySelector('.ps-quiz__progress-fill') : null;
      this.back = this.querySelector('[data-ps-quiz-back]');
      this.results = Array.from(this.querySelectorAll('[data-ps-quiz-state]'));
      this.template = this.dataset.progressTemplate || '';
      // Teile der Ergebnis-Panels, die beim Füllen in die eingesetzte Karte wandern (Referenz bleibt erhalten)
      this.parts = new Map();
      this.results.forEach((panel) => {
        this.parts.set(panel, {
          details: panel.querySelector('[data-ps-quiz-slot="details"]'),
          second: panel.querySelector('[data-ps-quiz-slot="second"]'),
          secondLabel: panel.querySelector('[data-ps-quiz-slot="second-label"]'),
          secondDetails: panel.querySelector('[data-ps-quiz-slot="second-details"]')
        });
      });
      this.answers = {};
      this.index = 0;
      this.busy = false;
      this.addEventListener('click', (event) => this.onClick(event));
      this.update(false);
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || this.busy) return;
      const option = target.closest('[data-ps-quiz-answer]');
      if (option && this.contains(option)) {
        // Doppelklick: Der zweite Klick träfe nach dem Weiterschalten die nächste Frage an derselben Stelle – nur Klicks
        // derselben Folge auf derselben Frage zählen (Mehrfachauswahl bleibt umschaltbar; Tastatur: detail 0)
        const step = option.closest('[data-ps-quiz-step]');
        if (event.detail <= 1) this.clickStep = step;
        else if (step !== this.clickStep) return;
        this.choose(option);
        return;
      }
      const add = target.closest('[data-ps-quiz-add]');
      if (add && this.contains(add)) {
        this.addToCart(add);
        return;
      }
      if (target.closest('[data-ps-quiz-next]')) {
        this.next();
        return;
      }
      if (target.closest('[data-ps-quiz-back]')) {
        this.previous();
        return;
      }
      if (target.closest('[data-ps-quiz-restart]')) this.restart();
    }

    get current() {
      return this.steps[this.index];
    }

    keyOf(step) {
      return step.dataset.psQuizStep;
    }

    limitOf(step) {
      return Number(step.dataset.psQuizMulti) || 0;
    }

    chosen(step) {
      const value = this.answers[this.keyOf(step)];
      return Array.isArray(value) ? value : [];
    }

    choose(option) {
      const step = option.closest('[data-ps-quiz-step]');
      if (step !== this.current || option.getAttribute('aria-disabled') === 'true') return;
      const key = this.keyOf(step);
      const value = option.dataset.psQuizAnswer;
      const limit = this.limitOf(step);
      if (limit > 0) {
        const chosen = this.chosen(step).slice();
        const position = chosen.indexOf(value);
        if (position >= 0) chosen.splice(position, 1);
        else if (chosen.length < limit) chosen.push(value);
        else return;
        this.answers[key] = chosen;
        this.sync(step);
        return;
      }
      this.answers[key] = value;
      this.sync(step);
      this.next();
    }

    answered(step) {
      if (this.limitOf(step) > 0) return this.chosen(step).length > 0;
      return typeof this.answers[this.keyOf(step)] === 'string';
    }

    next() {
      if (!this.answered(this.current)) return;
      if (this.index < this.steps.length - 1) {
        this.index += 1;
        this.update(true);
        return;
      }
      this.finish();
    }

    previous() {
      if (this.index === 0) return;
      this.index -= 1;
      this.update(true);
    }

    restart() {
      this.answers = {};
      this.index = 0;
      this.steps.forEach((step) => this.sync(step));
      this.update(true);
    }

    sync(step) {
      const limit = this.limitOf(step);
      const single = this.answers[this.keyOf(step)];
      const chosen = this.chosen(step);
      step.querySelectorAll('[data-ps-quiz-answer]').forEach((option) => {
        const id = option.dataset.psQuizAnswer;
        if (limit > 0) {
          const selected = chosen.includes(id);
          option.setAttribute('aria-checked', String(selected));
          if (!selected && chosen.length >= limit) option.setAttribute('aria-disabled', 'true');
          else option.removeAttribute('aria-disabled');
        } else {
          option.setAttribute('aria-pressed', String(single === id));
        }
      });
      const nextButton = step.querySelector('[data-ps-quiz-next]');
      if (nextButton) nextButton.disabled = chosen.length === 0;
    }

    update(moveFocus) {
      this.results.forEach((panel) => {
        panel.hidden = true;
      });
      this.card.hidden = false;
      this.steps.forEach((step, position) => {
        step.hidden = position !== this.index;
      });
      this.sync(this.current);
      const total = this.steps.length;
      const number = this.index + 1;
      const label = this.template.replace('{step}', String(number)).replace('{total}', String(total));
      if (this.count && label) this.count.textContent = label;
      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', String(number));
        this.progress.setAttribute('aria-valuemax', String(total));
        if (label) this.progress.setAttribute('aria-valuetext', label);
      }
      if (this.fill) this.fill.style.setProperty('--ps-quiz-progress', `${(number / total) * 100}%`);
      if (this.back) this.back.hidden = this.index === 0;
      if (moveFocus) this.reveal(this.current, this.card);
    }

    panelFor(state) {
      return this.results.find((item) => item.dataset.psQuizState === state) || null;
    }

    async finish() {
      this.busy = true;
      let outcome = null;
      try {
        outcome = await this.evaluate();
      } catch (error) {
        outcome = null;
      }
      this.busy = false;
      let panel = outcome ? this.panelFor(outcome.state) : null;
      if (panel && WITH_ITEM.includes(outcome.state)) {
        try {
          this.render(panel, outcome);
        } catch (error) {
          panel = null;
        }
      }
      if (!panel) panel = this.panelFor('not_configured');
      if (!panel) return;
      this.card.hidden = true;
      this.results.forEach((item) => {
        item.hidden = item !== panel;
      });
      this.reveal(panel, panel);
    }

    snapshot() {
      const answers = {};
      this.steps.forEach((step) => {
        const key = this.keyOf(step);
        const value = this.answers[key];
        if (Array.isArray(value)) answers[key] = value.slice();
        else if (typeof value === 'string') answers[key] = value;
      });
      return answers;
    }

    async evaluate() {
      const PS = window.Petstyle24;
      const engine = PS && PS.quizEngine;
      if (!engine || typeof engine.evaluate !== 'function') return null;
      const data = PS.productData && typeof PS.productData.read === 'function' ? PS.productData.read(this) : null;
      if (!data || !data.ok) return null;
      let result = null;
      try {
        result = await withTimeout(
          Promise.resolve(engine.evaluate({ schema: SCHEMA, answers: this.snapshot(), products: data.products })),
          ENGINE_TIMEOUT
        );
      } catch (error) {
        result = null;
      }
      return this.validate(result, data.products);
    }

    /* Streng nach UI-Vertrag: alles Unbekannte → null (= not_configured) */
    validate(result, items) {
      if (!isObject(result) || !STATES.includes(result.state) || !this.panelFor(result.state)) return null;
      if (!WITH_ITEM.includes(result.state)) return { state: result.state };
      const byId = new Map(items.map((item) => [item.id, item]));
      const entry = (raw) => {
        if (!isObject(raw) || !isId(raw.id) || !BUY.includes(raw.buy)) return false;
        const item = byId.get(raw.id);
        if (!item || !this.cardFor(raw.id)) return false;
        if (raw.buy === 'add') {
          const match = item.variants.find((variant) => variant.id === raw.variant);
          if (!match || !match.available) return false;
        }
        return { raw, item };
      };
      const primary = entry(result.primary);
      const alternative = result.alternative === null || result.alternative === undefined ? null : entry(result.alternative);
      if (!primary || alternative === false) return null;
      if (alternative && alternative.raw.id === primary.raw.id) return null;
      if (result.state === 'alt' && !alternative) return null;
      const size = result.size;
      if (!isObject(size) || !SIZE_KEYS.includes(size.textKey) || ![4.5, 9, null].includes(size.value)) return null;
      const notices = Array.isArray(result.notices) ? result.notices.filter(isObject) : [];
      const first = notices.find((notice) => notice.key === 'first_choice_sold_out');
      if (first && !this.cardFor(first.product)) return null;
      return {
        state: result.state,
        primary,
        alternative: result.state === 'clear' ? null : alternative,
        reasons: Array.isArray(result.reasons) ? result.reasons.filter(isObject) : [],
        size,
        notices
      };
    }

    texts() {
      if (this.textCache) return this.textCache;
      const node = this.querySelector('script[type="application/json"][data-ps-quiz-text]');
      if (!node) throw new Error('texts');
      this.textCache = JSON.parse(node.textContent || '');
      return this.textCache;
    }

    cardFor(id) {
      if (!isId(id)) return null;
      const holder = this.querySelector('template[data-ps-quiz-cards]');
      if (!holder) return null;
      return holder.content.querySelector(`[data-ps-quiz-card="${id}"]`);
    }

    titleOf(id) {
      const node = this.cardFor(id);
      const title = node ? node.querySelector('.product-card__title') : null;
      return title ? title.textContent.trim() : '';
    }

    cloneCard(id) {
      const wrapper = document.importNode(this.cardFor(id), true);
      const article = wrapper.querySelector('.product-card');
      const body = article ? article.querySelector('.product-card__body') : null;
      const footer = article ? article.querySelector('.product-card__footer') : null;
      if (!article || !body || !footer) throw new Error('card');
      return { wrapper, article, body, footer };
    }

    label(map, code) {
      const value = map && typeof map[code] === 'string' ? map[code] : '';
      if (!value) throw new Error('label');
      return value;
    }

    list(map, codes, joiner) {
      return codes.map((code) => this.label(map, code)).join(joiner);
    }

    /* Gründe (§12, QZ-10 bis QZ-12): Treffer mit Häkchen, fehlende Punkte mit Minus */
    reasonRows(state, primary, reasons) {
      const t = this.texts();
      const rows = [];
      if (state !== 'unclear') {
        reasons.forEach((reason) => {
          const codes = Array.isArray(reason.codes) ? reason.codes : [];
          if (!codes.length) return;
          if (reason.type === 'function') rows.push({ hit: true, text: fillIn(t.reasonFunction, this.list(t.function, codes, t.listJoiner)) });
          if (reason.type === 'goal') rows.push({ hit: true, text: fillIn(t.reasonGoal, this.list(t.goal, codes, t.listJoiner)) });
          if (reason.type === 'profile') rows.push({ hit: true, text: fillIn(t.reasonCoat, this.list(t.coat, codes, t.listJoiner)) });
        });
        return rows;
      }
      const m = isObject(primary.raw.matches) ? primary.raw.matches : {};
      const goals = Array.isArray(m.goals) ? m.goals : [];
      const functions = Array.isArray(m.functions) ? m.functions : [];
      const missing = Array.isArray(m.missing) ? m.missing : [];
      if (goals.length) rows.push({ hit: true, text: fillIn(t.reasonGoal, this.list(t.goal, goals, t.listJoiner)) });
      if (functions.length) rows.push({ hit: true, text: fillIn(t.reasonFunction, this.list(t.function, functions, t.listJoiner)) });
      if (typeof m.profile === 'string') rows.push({ hit: true, text: fillIn(t.reasonCoat, this.label(t.coat, m.profile)) });
      else if (m.coatStated === true) rows.push({ hit: false, text: t.reasonCoatNotStated });
      missing.forEach((code) => rows.push({ hit: false, text: fillIn(t.reasonFunctionMissing, this.label(t.function, code)) }));
      return rows;
    }

    /* Text im zweiten Kasten: „Besser, wenn …“ (Alternative) bzw. Lesart oder Treffer (Zweite Option) */
    secondText(state, second) {
      const t = this.texts();
      const raw = second.raw;
      if (state === 'alt') {
        const because = isObject(raw.because) ? raw.because : {};
        const feature = isObject(because.feature) && because.feature.type === 'function' ? because.feature.code : null;
        if (because.rule === 'A1') return t.betterA1;
        if (because.rule === 'A2') return t.betterA2;
        if (because.rule === 'A4' && feature) return fillIn(t.betterPlus, this.label(t.functionInline, feature));
        if ((because.rule === 'A3' || because.rule === 'E5') && feature) return fillIn(t.betterFeature, this.label(t.functionInline, feature));
        return '';
      }
      if (raw.reading) return this.label(t.reading, raw.reading);
      return this.reasonRows('unclear', second, []).map((row) => row.text).join(t.listJoiner);
    }

    render(panel, outcome) {
      const t = this.texts();
      const parts = this.parts.get(panel);
      const { state, primary, alternative, size, notices } = outcome;
      const main = panel.querySelector('[data-ps-quiz-slot="primary"]');
      if (!parts || !parts.details || !main) throw new Error('panel');

      // „Nicht eindeutig“ mit einer Möglichkeit: eigene Überschrift ohne Statuszeile (H4, 10B)
      const variant = state === 'unclear' && !alternative ? 'single' : 'pair';
      panel.querySelectorAll('[data-ps-quiz-variant]').forEach((node) => {
        node.hidden = node.dataset.psQuizVariant !== variant;
      });

      // Hinweise H2 (erste Wahl ausverkauft) und H3 (keine Ein-Bürsten-Lösung)
      panel.querySelectorAll('[data-ps-quiz-notice]').forEach((node) => {
        const notice = notices.find((item) => item.key === node.dataset.psQuizNotice);
        const slot = node.querySelector('[data-ps-quiz-slot="notice-text"]');
        node.hidden = !notice || !slot;
        if (node.hidden) return;
        if (notice.key === 'first_choice_sold_out') {
          const title = this.titleOf(notice.product);
          if (!title) throw new Error('title');
          slot.textContent = fillIn(t.noticeFirstChoice, title);
        } else {
          const codes = Array.isArray(notice.functions) ? notice.functions : [];
          slot.textContent = fillIn(t.noticeNoSingle, this.list(t.functionInline, codes, t.functionsJoiner));
        }
      });

      // Hauptempfehlung bzw. Möglichkeit 1: Karte einsetzen, Angaben vor den Kartenfuß
      const card = this.cloneCard(primary.raw.id);
      main.replaceChildren(card.wrapper);
      card.article.insertBefore(parts.details, card.footer);
      const slot = (name) => parts.details.querySelector(`[data-ps-quiz-slot="${name}"]`);

      const reading = slot('primary-if');
      if (reading) {
        reading.textContent = primary.raw.reading ? this.label(t.reading, primary.raw.reading) : '';
        reading.hidden = !primary.raw.reading;
      }
      const residual = slot('residual');
      if (residual) residual.hidden = !notices.some((item) => item.key === 'residual_stock' && item.product === primary.raw.id);

      const sizeLabel = slot('size-label');
      const sizeWhy = slot('size-why');
      if (sizeLabel) sizeLabel.textContent = fillIn(t.sizeLabel, size.value === null ? t.size.both : this.label(t.size, String(size.value)));
      if (sizeWhy) sizeWhy.textContent = this.label(t.sizeWhy, size.textKey);

      const rows = this.reasonRows(state, primary, outcome.reasons);
      // keine Treffer (z. B. E2: nur angenommene Antworten, REGELN §3 Nr. 7) → kein leerer „Abgleich“-Block
      const why = parts.details.querySelector('.ps-quiz-result__why');
      if (why) why.hidden = rows.length === 0;
      parts.details.querySelectorAll('[data-ps-quiz-reason]').forEach((row, position) => {
        const entry = rows[position];
        const text = row.querySelector('[data-ps-quiz-slot="reason-text"]');
        row.hidden = !entry;
        row.classList.toggle('ps-quiz-result__reason--miss', Boolean(entry && !entry.hit));
        row.querySelectorAll('[data-ps-quiz-reason-icon]').forEach((icon) => {
          icon.hidden = !entry || (icon.dataset.psQuizReasonIcon === 'hit') !== entry.hit;
        });
        if (text) text.textContent = entry ? entry.text : '';
      });

      this.renderPurchase(card, primary, size);

      // Alternative bzw. Zweite Option (ohne Kaufen, FR-9) – in der Karte nach den Knöpfen, wie im Design (QuizResult)
      const box = parts.second;
      if (box) {
        card.article.appendChild(box);
        const holder = box.querySelector('[data-ps-quiz-slot="second-card"]');
        box.hidden = !alternative;
        if (holder) holder.replaceChildren();
        if (alternative && holder && parts.secondDetails) {
          const second = this.cloneCard(alternative.raw.id);
          second.article.querySelectorAll('img[sizes]').forEach((image) => image.setAttribute('sizes', '64px'));
          if (parts.secondLabel) second.body.insertBefore(parts.secondLabel, second.body.firstChild);
          second.article.insertBefore(parts.secondDetails, second.footer);
          holder.replaceChildren(second.wrapper);
          const soldOut = parts.secondDetails.querySelector('[data-ps-quiz-slot="second-sold-out"]');
          if (soldOut) soldOut.hidden = alternative.raw.available !== false;
          const text = parts.secondDetails.querySelector('[data-ps-quiz-slot="second-text"]');
          if (text) {
            text.textContent = this.secondText(state, alternative);
            text.hidden = !text.textContent;
          }
        }
      }
    }

    /* Preis der empfohlenen Größe, Verfügbarkeit und genau ein Kauf-Weg (FR-9, 9A, V4) */
    renderPurchase(card, primary, size) {
      const t = this.texts();
      const raw = primary.raw;
      let priceKey = '';
      if (raw.buy === 'add') {
        priceKey = String(raw.variant);
      } else if (size.value !== null) {
        const same = primary.item.variants.filter((variant) => variant.width === size.value);
        if (same.length === 1) priceKey = String(same[0].id);
      }
      card.article.querySelectorAll('[data-ps-card-price]').forEach((node) => {
        node.hidden = node.dataset.psCardPrice !== priceKey;
      });
      const availability = card.article.querySelector('[data-ps-card-availability]');
      if (availability) {
        availability.textContent = raw.available === true ? t.available : t.soldOut;
        availability.hidden = false;
      }
      card.article.querySelectorAll('.product-card__buy').forEach((node) => {
        node.hidden = !(raw.buy === 'choose' && node.tagName === 'A');
      });
      const add = card.article.querySelector('[data-ps-quiz-add]');
      if (add) {
        add.hidden = raw.buy !== 'add';
        if (raw.buy === 'add') add.setAttribute('data-ps-quiz-variant', String(raw.variant));
      }
      if (raw.buy === 'add') {
        card.article.querySelectorAll('.product-card__media, .product-card__title a, [data-ps-card-view]').forEach((link) => {
          const href = link.getAttribute('href');
          if (href) link.setAttribute('href', withVariant(href, raw.variant));
        });
      }
    }

    async addToCart(button) {
      const PS = window.Petstyle24;
      const variant = Number(button.getAttribute('data-ps-quiz-variant'));
      if (!PS || !PS.cart || typeof PS.cart.add !== 'function' || !isId(variant)) return;
      if (button.getAttribute('aria-disabled') === 'true') return;
      const strings = (PS.config && PS.config.strings) || {};
      const error = button.closest('.product-card') ? button.closest('.product-card').querySelector('[data-ps-card-error]') : null;
      if (error) error.textContent = '';
      // Laden: aria-disabled statt disabled, damit der Tastaturfokus auf dem Button bleibt
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      setLabel(button, strings.adding);

      const result = await PS.cart.add(variant, 1, { trigger: button });
      if (!button.isConnected) return;

      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      if (!result || !result.ok) {
        setLabel(button, strings.addToCart);
        if (error) error.textContent = (result && result.error) || strings.errorGeneric || '';
        return;
      }
      setIcon(button, true);
      setLabel(button, strings.added);
      window.setTimeout(() => {
        if (!button.isConnected) return;
        setIcon(button, false);
        setLabel(button, strings.addToCart);
      }, ADDED_MS);
    }

    reveal(container, anchor) {
      const heading = container.querySelector('[data-ps-quiz-heading]');
      if (heading) heading.focus({ preventScroll: true });
      const top = anchor.getBoundingClientRect().top;
      const offset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      if (top < offset || top > window.innerHeight * 0.5) {
        anchor.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    }
  }

  customElements.define('ps-brush-quiz', PsBrushQuiz);
})();
